import { useEffect, useMemo, useState } from "react";
import { X, Loader2, Search, Users, Check } from "lucide-react";
import { toast } from "react-toastify";
import { getConfig, saveConfig, listGrupos } from "../services/whatsappChatApi";

// Modal de configuração de gravação por sessão (usado em /whatsapp/sessoes).
// onSaved(config) devolve { gravar_mensagens, gravar_grupos, gravar_grupos_lista }.
export default function GravacaoModal({ sessao, onClose, onSaved }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [gravarMensagens, setGravarMensagens] = useState(true);
  const [gravarGrupos, setGravarGrupos] = useState("nenhum");
  const [selecionados, setSelecionados] = useState([]); // [{jid, nome}]
  const [grupos, setGrupos] = useState(null); // null = ainda não carregado
  const [loadingGrupos, setLoadingGrupos] = useState(false);
  const [erroGrupos, setErroGrupos] = useState(null);
  const [busca, setBusca] = useState("");

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    getConfig(sessao.id)
      .then((cfg) => {
        if (cancel) return;
        setGravarMensagens(cfg.gravar_mensagens !== false);
        setGravarGrupos(cfg.gravar_grupos || "nenhum");
        setSelecionados(Array.isArray(cfg.gravar_grupos_lista) ? cfg.gravar_grupos_lista : []);
      })
      .catch(() => {
        if (cancel) return;
        setGravarMensagens(sessao.gravar_mensagens !== false);
        setGravarGrupos(sessao.gravar_grupos || "nenhum");
        setSelecionados(Array.isArray(sessao.gravar_grupos_lista) ? sessao.gravar_grupos_lista : []);
      })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [sessao]);

  useEffect(() => {
    if (gravarGrupos !== "lista" || grupos !== null || loadingGrupos) return;
    setLoadingGrupos(true);
    setErroGrupos(null);
    listGrupos(sessao.id)
      .then((lista) => setGrupos(lista))
      .catch((err) => {
        setGrupos([]);
        setErroGrupos(err?.response?.data?.error?.message || "Não foi possível listar os grupos (sessão conectada?)");
      })
      .finally(() => setLoadingGrupos(false));
  }, [gravarGrupos, grupos, loadingGrupos, sessao.id]);

  const selecionadosSet = useMemo(() => new Set(selecionados.map((g) => g.jid)), [selecionados]);

  const gruposFiltrados = useMemo(() => {
    const lista = grupos || [];
    const q = busca.trim().toLowerCase();
    const base = q ? lista.filter((g) => (g.nome || "").toLowerCase().includes(q) || (g.jid || "").includes(q)) : lista;
    // Grupos já selecionados que não vieram na listagem (ex.: sessão desconectada) continuam visíveis.
    const faltantes = selecionados.filter((s) => !lista.some((g) => g.jid === s.jid));
    return [...faltantes.filter((f) => !q || (f.nome || "").toLowerCase().includes(q)), ...base];
  }, [grupos, busca, selecionados]);

  function toggleGrupo(g) {
    setSelecionados((prev) =>
      prev.some((p) => p.jid === g.jid) ? prev.filter((p) => p.jid !== g.jid) : [...prev, { jid: g.jid, nome: g.nome || g.jid }],
    );
  }

  async function handleSalvar() {
    setSaving(true);
    try {
      const body = {
        gravar_mensagens: gravarMensagens,
        gravar_grupos: gravarGrupos,
        gravar_grupos_lista: gravarGrupos === "lista" ? selecionados.map((g) => ({ jid: g.jid, nome: g.nome })) : [],
      };
      const salvo = await saveConfig(sessao.id, body);
      toast.success("Configuração de gravação salva");
      onSaved?.({ ...body, ...(salvo || {}) });
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.error?.message || "Erro ao salvar configuração");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" role="dialog" aria-modal="true" aria-label="Configuração de gravação">
      <div className="bg-surface-1 border border-white/[0.08] rounded-2xl p-6 w-full max-w-lg max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between mb-1">
          <h2 className="text-xl font-bold font-display">Gravação de mensagens</h2>
          <button onClick={onClose} className="text-ink-3 hover:text-white" aria-label="Fechar">
            <X className="w-5 h-5" />
          </button>
        </div>
        <p className="text-xs text-ink-4 mb-5 truncate">Sessão <span className="text-ink-2">{sessao.nome}</span>{sessao.numero ? ` · ${sessao.numero}` : ""}</p>

        {loading ? (
          <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-ink-4" /></div>
        ) : (
          <div className="space-y-5 overflow-y-auto scrollbar-thin pr-1 flex-1 min-h-0">
            {/* Toggle geral */}
            <label className="flex items-start justify-between gap-4 cursor-pointer">
              <span>
                <span className="block text-sm font-medium text-ink-1">Gravar mensagens desta sessão</span>
                <span className="block text-xs text-ink-4 mt-0.5">
                  Salva no Metriqa tudo que entra e sai: respostas dos clientes, envios pela API e pelo celular, anexos.
                </span>
              </span>
              <button
                type="button"
                role="switch"
                aria-checked={gravarMensagens}
                onClick={() => setGravarMensagens((v) => !v)}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 ${gravarMensagens ? "bg-brand-600" : "bg-surface-4"}`}
              >
                <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${gravarMensagens ? "translate-x-5" : "translate-x-0.5"}`} />
              </button>
            </label>

            {/* Grupos */}
            <fieldset className={`${gravarMensagens ? "" : "opacity-50 pointer-events-none"}`} disabled={!gravarMensagens}>
              <legend className="text-sm font-medium text-ink-1 mb-2">Grupos</legend>
              <div className="grid grid-cols-3 gap-2" role="radiogroup" aria-label="Gravar grupos">
                {[
                  { id: "nenhum", label: "Nenhum", desc: "Só conversas individuais" },
                  { id: "todos", label: "Todos", desc: "Todos os grupos da sessão" },
                  { id: "lista", label: "Escolher", desc: "Selecionar grupos" },
                ].map((op) => {
                  const ativo = gravarGrupos === op.id;
                  return (
                    <button
                      key={op.id}
                      type="button"
                      role="radio"
                      aria-checked={ativo}
                      onClick={() => setGravarGrupos(op.id)}
                      className={`relative text-left rounded-xl border p-3 transition ${ativo ? "border-brand-500/60 bg-brand-500/10 shadow-glow-sm" : "border-white/[0.08] bg-surface-2 hover:border-white/[0.16]"}`}
                    >
                      {ativo && (
                        <span className="absolute top-2 right-2 h-4 w-4 rounded-full bg-brand-500 grid place-items-center">
                          <Check className="h-2.5 w-2.5 text-white" strokeWidth={3} />
                        </span>
                      )}
                      <p className="text-sm font-medium text-ink-1">{op.label}</p>
                      <p className="text-[11px] text-ink-4 mt-0.5">{op.desc}</p>
                    </button>
                  );
                })}
              </div>

              {gravarGrupos === "lista" && (
                <div className="mt-3 rounded-xl border border-white/[0.08] bg-surface-2 overflow-hidden">
                  <div className="p-2 border-b border-white/[0.06] flex items-center gap-2">
                    <div className="relative flex-1">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-ink-5 pointer-events-none" />
                      <input
                        type="search"
                        value={busca}
                        onChange={(e) => setBusca(e.target.value)}
                        placeholder="Buscar grupo..."
                        aria-label="Buscar grupo"
                        className="w-full bg-surface-3 border border-white/[0.08] rounded-lg text-xs text-ink-2 pl-8 pr-3 py-1.5 placeholder:text-ink-5 focus:outline-none focus:border-brand-600"
                      />
                    </div>
                    <span className="text-[11px] text-ink-4 whitespace-nowrap tabular-nums">{selecionados.length} selecionado{selecionados.length === 1 ? "" : "s"}</span>
                  </div>
                  <div className="max-h-56 overflow-y-auto scrollbar-thin">
                    {loadingGrupos ? (
                      <div className="flex justify-center py-6"><Loader2 className="w-4 h-4 animate-spin text-ink-4" /></div>
                    ) : erroGrupos && gruposFiltrados.length === 0 ? (
                      <p className="text-xs text-danger-400 p-3">{erroGrupos}</p>
                    ) : gruposFiltrados.length === 0 ? (
                      <p className="text-xs text-ink-4 p-3">Nenhum grupo encontrado.</p>
                    ) : (
                      gruposFiltrados.map((g) => {
                        const marcado = selecionadosSet.has(g.jid);
                        return (
                          <label key={g.jid} className="flex items-center gap-3 px-3 py-2 border-b border-white/[0.04] last:border-b-0 hover:bg-surface-3 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={marcado}
                              onChange={() => toggleGrupo(g)}
                              className="h-4 w-4 rounded border-white/20 bg-surface-3 text-brand-500 focus:ring-brand-500"
                            />
                            <Users className="w-4 h-4 text-ink-4 flex-shrink-0" />
                            <span className="flex-1 min-w-0">
                              <span className="block text-xs text-ink-1 truncate">{g.nome || g.jid}</span>
                              {g.participantes != null && (
                                <span className="block text-[10px] text-ink-4 tabular-nums">{g.participantes} participante{g.participantes === 1 ? "" : "s"}</span>
                              )}
                            </span>
                          </label>
                        );
                      })
                    )}
                  </div>
                  {erroGrupos && gruposFiltrados.length > 0 && <p className="text-[11px] text-warning-400 px-3 py-2 border-t border-white/[0.06]">{erroGrupos}</p>}
                </div>
              )}
            </fieldset>
          </div>
        )}

        <div className="flex gap-3 mt-6 flex-shrink-0">
          <button onClick={onClose} className="btn-ghost flex-1 justify-center" disabled={saving}>Cancelar</button>
          <button onClick={handleSalvar} disabled={saving || loading} className="btn-primary flex-1 justify-center">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Salvar"}
          </button>
        </div>
      </div>
    </div>
  );
}
